import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createMessage,
  listMyMessages,
  replyToMessage,
  deleteMessage,
} from "../../Redux/Actions/MessageActions";
import { MESSAGE_CREATE_RESET, MESSAGE_REPLY_RESET } from "../../Redux/Constants/MessageConstants";
import Loading from "../LoadingError/Loading";
import Message from "../LoadingError/Error";
import { toast } from "react-toastify";
import moment from "moment";
import "./Messages.css";

const Messages = () => {
  const [subject, setSubject] = useState("");
  const [messageText, setMessageText] = useState("");
  const [priority, setPriority] = useState("normal");
  const [showForm, setShowForm] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [selectedMessageId, setSelectedMessageId] = useState(null);

  const dispatch = useDispatch();

  const messageCreate = useSelector((state) => state.messageCreate);
  const { loading: createLoading, success: createSuccess, error: createError } = messageCreate;

  const messageMyList = useSelector((state) => state.messageMyList);
  const { loading, error, messages } = messageMyList;

  const messageDelete = useSelector((state) => state.messageDelete);
  const { success: deleteSuccess } = messageDelete;

  const messageReply = useSelector((state) => state.messageReply);
  const { success: replySuccess, error: replyError } = messageReply;

  const toastId = React.useRef(null);

  const Toastobjects = useMemo(() => ({
    pauseOnFocusLoss: false,
    draggable: false,
    pauseOnHover: false,
    autoClose: 2000,
  }), []);

  const unreadCount = messages?.filter(msg => {
    if (msg.replies && msg.replies.length > 0) {
      const lastReply = msg.replies[msg.replies.length - 1];
      return lastReply.sender === 'admin' && msg.status === 'replied';
    }
    return msg.adminReply && msg.status === 'replied';
  }).length || 0;

  useEffect(() => {
    dispatch(listMyMessages());
    const interval = setInterval(() => {
      dispatch(listMyMessages());
    }, 30000);
    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    if (createSuccess) {
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.success("Message sent successfully!", Toastobjects);
      }
      setSubject("");
      setMessageText("");
      setPriority("normal");
      setShowForm(false);
      dispatch({ type: MESSAGE_CREATE_RESET });
      dispatch(listMyMessages());
    }
    if (createError) {
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.error(createError, Toastobjects);
      }
    }
  }, [createSuccess, createError, dispatch, Toastobjects]);

  useEffect(() => {
    if (deleteSuccess) {
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.success("Message removed from your inbox!", Toastobjects);
      }
      dispatch(listMyMessages());
    }
  }, [deleteSuccess, dispatch, Toastobjects]);

  useEffect(() => {
    if (replySuccess) {
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.success("Reply sent successfully!", Toastobjects);
      }
      setReplyText("");
      setSelectedMessageId(null);
      dispatch({ type: MESSAGE_REPLY_RESET });
      dispatch(listMyMessages());
    }
    if (replyError) {
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.error(replyError, Toastobjects);
      }
    }
  }, [replySuccess, replyError, dispatch, Toastobjects]);

  useEffect(() => {
    if (unreadCount > 0 && !toast.isActive('unread-notification')) {
      toast.info(
        `You have ${unreadCount} unread ${unreadCount === 1 ? 'message' : 'messages'}!`,
        { ...Toastobjects, toastId: 'unread-notification', autoClose: 5000 }
      );
    }
  }, [unreadCount, Toastobjects]);

  const submitHandler = (e) => {
    e.preventDefault();
    if (!subject || !messageText) {
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.error("Please fill in all fields", Toastobjects);
      }
      return;
    }
    dispatch(createMessage(subject, messageText, priority));
  };

  const handleReplySubmit = (e, messageId) => {
    e.preventDefault();
    if (!replyText.trim()) {
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.error("Please enter a reply", Toastobjects);
      }
      return;
    }
    dispatch(replyToMessage(messageId, replyText));
  };

  const handleHideMessage = (messageId) => {
    if (window.confirm("Remove this message from your inbox? This cannot be undone.")) {
      dispatch(deleteMessage(messageId));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      unread: { class: "badge-secondary", icon: "fa-envelope", text: "Unread" },
      read: { class: "badge-info", icon: "fa-envelope-open", text: "Read" },
      replied: { class: "badge-success", icon: "fa-reply", text: "Replied" },
      closed: { class: "badge-dark", icon: "fa-check-circle", text: "Closed" },
    };
    const badge = badges[status] || badges.unread;
    return (
      <span className={`badge ${badge.class}`}>
        <i className={`fas ${badge.icon}`}></i> {badge.text}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      low: { class: "badge-light", text: "Low" },
      normal: { class: "badge-primary", text: "Normal" },
      high: { class: "badge-warning", text: "High" },
      urgent: { class: "badge-danger", text: "Urgent" },
    };
    const badge = badges[priority] || badges.normal;
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  const hasUnreadAdminReply = (msg) => {
    if (msg.replies && msg.replies.length > 0) {
      const lastReply = msg.replies[msg.replies.length - 1];
      return lastReply.sender === 'admin' && msg.status === 'replied';
    }
    return msg.adminReply && msg.status === 'replied';
  };

  return (
    <div className="messages-container">

      {/* ── Header ── */}
      <div className="messages-header">
        <div className="messages-header-title">
          <h4>
            <i className="fas fa-comments"></i> Customer Support
          </h4>
          {unreadCount > 0 && (
            <span className="badge badge-danger">{unreadCount} New</span>
          )}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          <i className="fas fa-plus"></i> New Message
        </button>
      </div>

      {/* ── New Message Form ── */}
      {showForm && (
        <div className="message-form-card">
          <div className="card-header">
            <h5>
              <i className="fas fa-paper-plane"></i> Send New Message
            </h5>
            <button className="btn-close-form" onClick={() => setShowForm(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <form onSubmit={submitHandler} className="p-3">
            <div className="form-group mb-3">
              <label>Subject *</label>
              <input
                type="text"
                className="form-control"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject"
                required
              />
            </div>
            <div className="form-group mb-3">
              <label>Priority</label>
              <select
                className="form-control"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="form-group mb-3">
              <label>Message *</label>
              <textarea
                className="form-control"
                rows="5"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Enter your message"
                required
              ></textarea>
            </div>
            <button type="submit" className="btn btn-primary" disabled={createLoading}>
              {createLoading ? (
                <><i className="fas fa-spinner fa-spin"></i> Sending...</>
              ) : (
                <><i className="fas fa-paper-plane"></i> Send Message</>
              )}
            </button>
          </form>
        </div>
      )}

      {/* ── Messages List ── */}
      <div className="messages-list">
        {loading ? (
          <Loading />
        ) : error ? (
          <Message variant="alert-danger">{error}</Message>
        ) : messages.length === 0 ? (
          <div className="empty-messages">
            <i className="fas fa-inbox"></i>
            <p>No messages yet</p>
            <small>Click "New Message" to contact support</small>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg._id}
              className={`message-card ${hasUnreadAdminReply(msg) ? 'unread-highlight' : ''}`}
            >
              {/* ── Card Header ── */}
              <div className="message-card-header">
                <div className="message-info">
                  <h5>{msg.subject}</h5>
                  <div className="message-meta">
                    <span className="message-date">
                      <i className="fas fa-calendar"></i>
                      {moment(msg.createdAt).format("MMM DD, YYYY - h:mm A")}
                    </span>
                    {getPriorityBadge(msg.priority)}
                    {getStatusBadge(msg.status)}
                  </div>
                </div>
                {/* Hide button — soft deletes on client only, stays in admin dashboard */}
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => handleHideMessage(msg._id)}
                  title="Remove from inbox"
                >
                  <i className="fas fa-eye-slash"></i>
                </button>
              </div>

              {/* ── Card Body ── */}
              <div className="message-card-body">
                <div className="conversation-thread">

                  {/* Original User Message */}
                  <div className="message-bubble user-message">
                    <div className="message-bubble-header">
                      <i className="fas fa-user-circle"></i>
                      <strong>You</strong>
                      <span className="message-time">
                        {moment(msg.createdAt).format("MMM DD, h:mm A")}
                      </span>
                    </div>
                    <div className="message-bubble-content">{msg.message}</div>
                  </div>

                  {/* Replies */}
                  {msg.replies && msg.replies.length > 0 && msg.replies.map((reply, index) => (
                    <div
                      key={index}
                      className={`message-bubble ${reply.sender === 'admin' ? 'admin-message' : 'user-message'}`}
                    >
                      <div className="message-bubble-header">
                        {reply.sender === 'admin' ? (
                          <><i className="fas fa-headset"></i><strong>Support Team</strong></>
                        ) : (
                          <><i className="fas fa-user-circle"></i><strong>You</strong></>
                        )}
                        <span className="message-time">
                          {moment(reply.timestamp).format("MMM DD, h:mm A")}
                        </span>
                      </div>
                      <div className="message-bubble-content">{reply.text}</div>
                    </div>
                  ))}

                  {/* Legacy admin reply */}
                  {msg.adminReply && (!msg.replies || msg.replies.length === 0) && (
                    <div className="message-bubble admin-message">
                      <div className="message-bubble-header">
                        <i className="fas fa-headset"></i>
                        <strong>Support Team</strong>
                        <span className="message-time">
                          {moment(msg.repliedAt).format("MMM DD, h:mm A")}
                        </span>
                      </div>
                      <div className="message-bubble-content">{msg.adminReply}</div>
                    </div>
                  )}
                </div>

                {/* Reply Form */}
                {msg.status !== 'closed' && (
                  <div className="reply-section mt-3">
                    {selectedMessageId === msg._id ? (
                      <form onSubmit={(e) => handleReplySubmit(e, msg._id)}>
                        <div className="form-group mb-2">
                          <label><strong>Your Reply:</strong></label>
                          <textarea
                            className="form-control"
                            rows="3"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Type your reply here..."
                            required
                          ></textarea>
                        </div>
                        <div className="d-flex gap-2">
                          <button type="submit" className="btn btn-primary btn-sm">
                            <i className="fas fa-paper-plane"></i> Send Reply
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              setSelectedMessageId(null);
                              setReplyText("");
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setSelectedMessageId(msg._id)}
                      >
                        <i className="fas fa-reply"></i> Reply to this conversation
                      </button>
                    )}
                  </div>
                )}

                {msg.status === 'closed' && (
                  <div className="alert alert-info mt-3 mb-0">
                    <i className="fas fa-info-circle"></i> This conversation has been closed by support.
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Messages;